import React, { useState } from 'react';
import { ChevronRight } from 'lucide-react';
import { Card } from '../../ui/card';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import {
  buildQueueCategoryGroups,
  formatQueueDateTime,
  getCommandCenterStateLabel,
  getCommandCenterWorkspaceLabel,
  getQueueItemNavigation,
} from './commandCenterQueueUtils';

function QueueCategoryRow({ item, onOpenItem, onEditItem }) {
  const hasExactDestination = Boolean(getQueueItemNavigation(item));

  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50/70 px-4 py-3 dark:border-white/10 dark:bg-white/[0.03]">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-sm font-semibold text-slate-900 dark:text-white">{item.title || 'Untitled action'}</p>
            <Badge className="border-slate-200 bg-white text-slate-700 dark:border-white/10 dark:bg-black/20 dark:text-slate-200">
              {getCommandCenterWorkspaceLabel(item.workspace)}
            </Badge>
            <Badge className="border-slate-200 bg-white text-slate-700 dark:border-white/10 dark:bg-black/20 dark:text-slate-200">
              {getCommandCenterStateLabel(item.state)}
            </Badge>
          </div>
          <p className="mt-1.5 text-sm leading-6 text-slate-600 dark:text-gray-300">
            {item.detail || item.rank_reason || 'No additional context yet.'}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-3">
          {item.due_at ? (
            <span className="text-xs text-slate-500 dark:text-gray-400">
              Due {formatQueueDateTime(item.due_at)}
            </span>
          ) : null}
          {hasExactDestination ? (
            <Button type="button" variant="outline" onClick={() => onOpenItem(item)}>
              Open
            </Button>
          ) : (
            <Button type="button" variant="outline" disabled className="border-slate-200 text-slate-500 dark:border-white/10 dark:text-slate-400">
              No destination yet
            </Button>
          )}
          <Button type="button" variant="outline" onClick={() => onEditItem(item)}>
            Edit
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function QueueCategorySection({ queueState, onOpenItem, onEditItem }) {
  const [openCategories, setOpenCategories] = useState([]);
  const categories = buildQueueCategoryGroups(queueState);

  return (
    <div>
      <div className="mb-3">
        <h3 className="text-lg font-semibold text-slate-900 dark:text-white">Standing Categories</h3>
        <p className="text-sm text-slate-600 dark:text-gray-400">
          The same queue, scanned by standing work buckets after the top three are clear.
        </p>
      </div>

      <div className="grid gap-4">
        {categories.map((category) => {
          const isOpen = openCategories.includes(category.id);
          return (
            <Card key={category.id} className="border-slate-200 bg-white p-5 dark:border-white/10 dark:bg-[#171b20]">
              <button
                type="button"
                onClick={() => {
                  setOpenCategories((current) => (
                    current.includes(category.id)
                      ? current.filter((item) => item !== category.id)
                      : [...current, category.id]
                  ));
                }}
                className="flex w-full items-start justify-between gap-4 text-left"
              >
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-sky-200 bg-sky-50 text-xs font-semibold text-sky-700 dark:border-sky-400/20 dark:bg-sky-400/10 dark:text-sky-200">
                      {category.label.charAt(0)}
                    </span>
                    <h4 className="text-base font-semibold text-slate-900 dark:text-white">{category.label}</h4>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <Badge className="border-slate-200 bg-white text-slate-700 dark:border-white/10 dark:bg-black/20 dark:text-slate-200">
                      {category.counts.total} total
                    </Badge>
                    <Badge className="border-slate-200 bg-white text-slate-700 dark:border-white/10 dark:bg-black/20 dark:text-slate-200">
                      {category.counts.active} active
                    </Badge>
                    <Badge className="border-slate-200 bg-white text-slate-700 dark:border-white/10 dark:bg-black/20 dark:text-slate-200">
                      {category.counts.later} later
                    </Badge>
                    <Badge className="border-slate-200 bg-white text-slate-700 dark:border-white/10 dark:bg-black/20 dark:text-slate-200">
                      {category.counts.needs_review} review
                    </Badge>
                  </div>
                </div>
                <span className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 bg-slate-50 text-slate-700 dark:border-white/10 dark:bg-white/[0.04] dark:text-slate-200">
                  <ChevronRight className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-90' : ''}`} />
                </span>
              </button>

              {isOpen ? (
                <div className="mt-4 grid gap-3">
                  {category.items.length ? (
                    category.items.map((item) => (
                      <QueueCategoryRow key={item.queue_item_id} item={item} onOpenItem={onOpenItem} onEditItem={onEditItem} />
                    ))
                  ) : (
                    <div className="rounded-2xl border border-slate-200 bg-slate-50/70 px-4 py-3 text-sm text-slate-600 dark:border-white/10 dark:bg-white/[0.03] dark:text-gray-300">
                      Nothing is sitting in this category right now.
                    </div>
                  )}
                </div>
              ) : null}
            </Card>
          );
        })}
      </div>
    </div>
  );
}
